import {Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {MatRadioChange} from '@angular/material/radio';
import {isMultipleOfValidator} from '@application/validators/is-multiple-of-validator';
import {NameGeneratorProperty} from '@domain/custom-settings/name-generation-property';
import {NameGenerator} from '@domain/custom-settings/name-generator';
import {FontFamily} from '@domain/fonts/font-family.enum';
import {FontStyle} from '@domain/fonts/font-style.enum';
import {FontWeight} from '@domain/fonts/font-weight.enum';
import {fromReadableToEnumEntry} from '@domain/fonts/fromReadableToEnumKey';
import {Pattern} from '@domain/pattern';
import {ProductionOrderCustomSettings} from '@domain/production-schedule/custom-settings/production-order-custom-settings';
import {InitialPathLabel} from '@domain/production-schedule/initial-path-label';
import {JuteInformation} from '@domain/production-schedule/jute-information';
import {MachineQualityForProductionSchedule} from '@domain/production-schedule/machine-quality-for-production-schedule';
import {PathLabelInformation} from '@domain/production-schedule/path-label-information';
import {PathLabelInformationForPart} from '@domain/production-schedule/path-label-information-for-part';
import {PathLabelType} from '@domain/production-schedule/path-label-type.enum';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription} from '@domain/profile/subscription';
import {FontSettings} from '@domain/textile-data/finishing-and-finishing-template/finishing/font-settings';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {HttpProductionOrdersCustomSettingsService} from '@infrastructure/http/production-order-custom-settings/http-production-orders-custom-settings.service';
import {PatternBuilderInput} from '@presentation/components/name-generation-pattern-new/pattern-builder/pattern-builder-input';
import {PatternBuilderComponent} from '@presentation/components/name-generation-pattern-new/pattern-builder/pattern-builder.component';
import {PatternType} from '@presentation/components/select-pattern/pattern-type.enum';
import {SelectPatternComponentMatDialogConfigDataInterface} from '@presentation/components/select-pattern/select-pattern-component-mat-dialog-config.interface';
import {SelectPatternComponent} from '@presentation/components/select-pattern/select-pattern.component';
import {OverviewListColoredYarnSet} from '@presentation/pages/textile-data/colored-yarn-set/overview/overview-list-colored-yarn-set';
import {WeaveQualityType} from '@presentation/pages/textile-data/machine-quality/overview/weave-quality-type.enum';
import {
  AssertionUtils,
  BaseComponent,
  ContentSwitcherNavigationInput,
  DialogBuilderFactoryService,
  EnumUtils,
  FormValueHelper,
  NavigationHelperPrototypeService,
  ObjectUtils,
  TranslateService,
  Unit
} from '@vdw/angular-component-library';
import {cloneDeep, isEqual} from 'lodash-es';
import {combineLatest, Subscription as RxjsSubscription} from 'rxjs';
import {startWith, takeUntil} from 'rxjs/operators';
import {AddProductionSchedule} from '../../../add-production-schedule';
import {ADD_PRODUCTION_SCHEDULE} from '../../../add-production-schedule.service';

@Component({
  selector: 'app-production-schedule-path-labels',
  templateUrl: './production-schedule-path-labels.component.html',
  styleUrls: ['./production-schedule-path-labels.component.scss']
})
export class ProductionSchedulePathLabelsComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() public machineQuality: MachineQualityForProductionSchedule;
  @Input() public customSettings: ProductionOrderCustomSettings;
  @Input() public pathLabelInformation: PathLabelInformation;
  @Input() public juteInformation: JuteInformation;
  @Input() public coloredYarnSet: OverviewListColoredYarnSet;
  @Input() public needsToShowValidationFeedback: boolean;
  @Input() public factorForConversionFromPicksToMillimeters: number;
  @Input() public factorForConversionFromMillimetersToPicks: number;
  @Input() public weaveQualityType: WeaveQualityType;
  @Input() public areAllFieldsDisabled: boolean = false;
  @Input({required: true}) public readonly defaultPathLabelInformation: InitialPathLabel;
  @Output() public formValuesChanged: EventEmitter<void> = new EventEmitter<void>();
  @Output() public pathLabelInformationChange: EventEmitter<PathLabelInformation> = new EventEmitter<PathLabelInformation>();
  @Output() public pathZoneLoaded = new EventEmitter();

  public readonly JUTE_PATTERN_CONDITIONAL_CHIP_VALUE_KEY = this.translate.instant('PRODUCTION_ORDER.WEAVE_PLAN.JUTE.PATTERN_NAME', {patternId: '%(id)'});

  public pathLabelsForm: FormGroup<{
    technicalLabelHeightInPicks: FormControl<number>;
    commercialLabelHeightInMM: FormControl<number>;
    pathLabelBefore: FormGroup<{
      pathLabelType: FormControl<PathLabelType>;
      nameGenerator: FormControl<NameGenerator>;
    }>;
    pathLabelAfter: FormGroup<{
      pathLabelType: FormControl<PathLabelType>;
      nameGenerator: FormControl<NameGenerator>;
    }>;
    labelFontFamily: FormControl<string>;
    labelFontWeight: FormControl<string>;
    labelFontStyle: FormControl<string>;
    pattern: FormControl<Pattern>;
  }>;

  public commercialUnit = Unit.MILLIMETER;
  public pathLabelNameGenerationProperties: NameGeneratorProperty[];

  public readonly pathLabelTypeText = PathLabelType.TEXT;
  public readonly pathLabelTypeQRCode = PathLabelType.QR_CODE;
  public readonly PRODUCTION_ORDER_SET_PATH_LABELS_COMMERCIAL_FONT_SIZE_PERMISSION: Permission = Permission.PRODUCTION_ORDER_SET_PATH_LABELS_COMMERCIAL_FONT_SIZE;
  public readonly addPathLabelPermission: Permission = Permission.ADD_PATH_LABEL;
  public possibleFontFamilies: FontFamily[] = EnumUtils.getEnumValues(FontFamily);
  public possibleFontWeights: FontWeight[] = EnumUtils.getEnumValues(FontWeight);
  public possibleFontStyles: FontStyle[] = EnumUtils.getEnumValues(FontStyle);
  public labelAfterPathIsSameAsBeforeCheckbox: boolean;

  private syncCommercialLabelHeightWithTechnicalLabelHeightInPicksSubscription: RxjsSubscription;
  private readonly MAX_ALLOWED_FONT_SIZE_FROM_BACKEND = 1000;

  private currentSubscription: Subscription;

  public constructor(
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    @Inject(ADD_PRODUCTION_SCHEDULE) private readonly addProductionSchedule: AddProductionSchedule,
    private readonly httpProductionOrderCustomSettings: HttpProductionOrdersCustomSettingsService,
    private readonly formBuilder: FormBuilder,
    private readonly translate: TranslateService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly navigationService: NavigationHelperPrototypeService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.currentSubscription = this.authentication.getCurrentSubscription();
    this.labelAfterPathIsSameAsBeforeCheckbox = this.isPathLabelAfterSameAsPathLabelBefore();

    this.setFormFields();
    this.setFormValues();
    this.getPathLabelNameGenerationProperties();

    this.listenToNameGeneratorChanges();
    this.listenToPathLabelTypeChanges();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('pathLabelInformation' in changes && !changes.pathLabelInformation.isFirstChange() && !isEqual(changes.pathLabelInformation.currentValue, changes.pathLabelInformation.previousValue)) {
      this.setFormValues();
      this.labelAfterPathIsSameAsBeforeCheckbox = this.isPathLabelAfterSameAsPathLabelBefore();
    }

    if ('machineQuality' in changes && !changes.machineQuality.isFirstChange()) {
      this.pathLabelsForm
        .get('technicalLabelHeightInPicks')
        .setValidators([
          Validators.required,
          isMultipleOfValidator(this.machineQuality.weaveStructure.picksPerColorLine),
          isMultipleOfValidator(this.machineQuality.weaveStructure.sendRepeat),
          Validators.min(this.machineQuality.weaveStructure.picksPerColorLine)
        ]);
    }

    if (
      ('machineQuality' in changes && !changes.machineQuality.isFirstChange()) ||
      ('factorForConversionFromPicksToMillimeters' in changes && !changes.factorForConversionFromPicksToMillimeters.isFirstChange()) ||
      ('factorForConversionFromMillimetersToPicks' in changes && !changes.factorForConversionFromMillimetersToPicks.isFirstChange())
    ) {
      this.syncCommercialLabelHeightWithTechnicalLabelHeightInPicks();
    }
  }

  private listenToNameGeneratorChanges(): void {
    const $nameGeneratorBefore = this.pathLabelsForm.controls.pathLabelBefore.controls.nameGenerator.valueChanges.pipe(startWith(this.pathLabelInformation?.pathLabelBefore?.nameGenerator));
    $nameGeneratorBefore.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((nameGenerator: NameGenerator) => {
      if (!AssertionUtils.isNullOrUndefined(this.pathLabelInformation)) {
        this.pathLabelInformation.pathLabelBefore.nameGenerator = nameGenerator;
      }

      if (this.labelAfterPathIsSameAsBeforeCheckbox) {
        this.pathLabelsForm.controls.pathLabelAfter.controls.nameGenerator.setValue(nameGenerator);
      }
    });

    const $nameGeneratorAfter = this.pathLabelsForm.controls.pathLabelAfter.controls.nameGenerator.valueChanges.pipe(startWith(this.pathLabelInformation?.pathLabelAfter?.nameGenerator));
    $nameGeneratorAfter.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((nameGenerator: NameGenerator) => {
      if (!AssertionUtils.isNullOrUndefined(this.pathLabelInformation)) {
        this.pathLabelInformation.pathLabelAfter.nameGenerator = nameGenerator;
      }
    });

    combineLatest([$nameGeneratorBefore, $nameGeneratorAfter])
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(([nameGeneratorBefore, nameGeneratorAfter]: [NameGenerator, NameGenerator]) => {
        if (
          (AssertionUtils.isNullOrUndefined(nameGeneratorBefore) || nameGeneratorBefore.parts.length === 0) &&
          (AssertionUtils.isNullOrUndefined(nameGeneratorAfter) || nameGeneratorAfter.parts.length === 0)
        ) {
          this.pathLabelInformation = null;
          this.pathLabelsForm.disable({emitEvent: false});
          this.pathZoneLoaded.emit();
        }

        this.formValuesChanged.emit();
        this.pathLabelsForm.markAsDirty();
        this.updatePathLabelInformation();
      });
  }

  private listenToPathLabelTypeChanges(): void {
    const $pathLabelBeforeType = this.pathLabelsForm.controls.pathLabelBefore.controls.pathLabelType.valueChanges.pipe(startWith(this.pathLabelInformation?.pathLabelBefore?.pathLabelType));

    $pathLabelBeforeType.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((pathLabelBeforeType: PathLabelType) => {
      if (this.labelAfterPathIsSameAsBeforeCheckbox) {
        this.pathLabelsForm.controls.pathLabelAfter.controls.pathLabelType.setValue(pathLabelBeforeType);
      }
    });

    combineLatest([$pathLabelBeforeType, this.pathLabelsForm.controls.pathLabelAfter.controls.pathLabelType.valueChanges.pipe(startWith(this.pathLabelInformation?.pathLabelAfter?.pathLabelType))])
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(([pathLabelBeforeType, pathLabelAfterType]: [PathLabelType, PathLabelType]) => {
        if (
          (pathLabelBeforeType === PathLabelType.QR_CODE || AssertionUtils.isNullOrUndefined(pathLabelBeforeType)) &&
          (pathLabelAfterType === PathLabelType.QR_CODE || AssertionUtils.isNullOrUndefined(pathLabelAfterType))
        ) {
          this.pathLabelsForm.controls.labelFontFamily.disable();
          this.pathLabelsForm.controls.labelFontWeight.disable();
          this.pathLabelsForm.controls.labelFontStyle.disable();
        } else if (!this.areAllFieldsDisabled) {
          this.pathLabelsForm.controls.labelFontFamily.enable();
          this.pathLabelsForm.controls.labelFontWeight.enable();
          this.pathLabelsForm.controls.labelFontStyle.enable();
        }
      });
  }

  public getMultipleValue(): number {
    if (this.machineQuality.weaveStructure.sendRepeat !== this.machineQuality.weaveStructure.picksPerColorLine) {
      return this.machineQuality.weaveStructure.sendRepeat * this.machineQuality.weaveStructure.picksPerColorLine;
    } else {
      return this.machineQuality.weaveStructure.sendRepeat;
    }
  }

  public getPathLabelInformation(): PathLabelInformation {
    if (
      this.pathLabelsForm.disabled ||
      (AssertionUtils.isNullOrUndefined(this.pathLabelsForm.value.pathLabelBefore?.nameGenerator) && AssertionUtils.isNullOrUndefined(this.pathLabelsForm.value.pathLabelAfter?.nameGenerator))
    ) {
      return null;
    }

    return new PathLabelInformation(
      new PathLabelInformationForPart(this.pathLabelsForm.value.pathLabelBefore.nameGenerator, this.pathLabelsForm.value.pathLabelBefore.pathLabelType),
      new PathLabelInformationForPart(this.pathLabelsForm.value.pathLabelAfter.nameGenerator, this.pathLabelsForm.value.pathLabelAfter.pathLabelType),
      new FontSettings(this.pathLabelsForm.get('labelFontFamily').value, this.pathLabelsForm.get('labelFontWeight').value, this.pathLabelsForm.get('labelFontStyle').value),
      this.pathLabelsForm.get('commercialLabelHeightInMM').value,
      this.pathLabelsForm.get('technicalLabelHeightInPicks').value,
      this.pathLabelsForm.get('pattern').value
    );
  }

  public isPathLabelAfterSameAsPathLabelBefore(): boolean {
    return (
      this.pathLabelInformation?.pathLabelAfter?.pathLabelType === this.pathLabelInformation?.pathLabelBefore?.pathLabelType &&
      isEqual(this.pathLabelInformation?.pathLabelAfter?.nameGenerator?.parts, this.pathLabelInformation?.pathLabelBefore?.nameGenerator?.parts) &&
      this.pathLabelInformation?.pathLabelAfter?.nameGenerator?.separatorName === this.pathLabelInformation?.pathLabelBefore?.nameGenerator?.separatorName
    );
  }

  public shouldShowPathLabelAfterComponent(): boolean {
    return !this.labelAfterPathIsSameAsBeforeCheckbox || !this.isPathLabelAfterSameAsPathLabelBefore();
  }

  public onLabelAfterPathIsSameAsBeforeChange(matCheckboxChange: MatCheckboxChange): void {
    this.labelAfterPathIsSameAsBeforeCheckbox = matCheckboxChange.checked;
    if (this.labelAfterPathIsSameAsBeforeCheckbox) {
      this.setPathLabelAfterAsPathLabelBefore();
      this.updatePathLabelInformation();
    }
  }

  public onLabelBeforePathTypeChange(matRadioChange: MatRadioChange): void {
    this.pathLabelInformation.pathLabelBefore.pathLabelType = matRadioChange.value;

    if (this.labelAfterPathIsSameAsBeforeCheckbox) {
      this.setPathLabelAfterAsPathLabelBefore();
    }

    this.updatePathLabelInformation();
  }

  public hasChosenPattern(): boolean {
    return this.pathLabelsForm.get('pattern').value != null;
  }

  public selectPattern(): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .openSelectGridDialog<SelectPatternComponentMatDialogConfigDataInterface<Pattern>, Pattern>(SelectPatternComponent, {
        patternType: PatternType.JUTE_PATTERNS,
        patternTranslationKey: 'GENERAL.PATTERN.PATTERN',
        showPatternTypeFilter: true,
        allowPatternCreation: false,
        weaveStructureId: this.machineQuality.weaveStructure.id,
        machineQualityId: this.machineQuality.id,
        colorSetId: this.coloredYarnSet.overviewListColorSet.id
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((pattern: Pattern | undefined) => {
        if (pattern !== undefined) {
          this.pathLabelsForm.controls.pattern.setValue(pattern);
          this.pathLabelInformation = this.getPathLabelInformation();
        }
      });
  }

  public getMaxAllowedFontSize(): number {
    const value = this.pathLabelsForm?.get('commercialLabelHeightInMM')?.value ? this.pathLabelsForm?.get('commercialLabelHeightInMM')?.value : 5;
    return value > this.MAX_ALLOWED_FONT_SIZE_FROM_BACKEND ? this.MAX_ALLOWED_FONT_SIZE_FROM_BACKEND : value;
  }

  public canShowMultipleError(): boolean {
    const errors = this.pathLabelsForm.get('technicalLabelHeightInPicks').errors;
    return errors?.multiple && !errors?.min;
  }

  public addPathLabels(): void {
    this.navigationService
      .navigateWithContentSwitcher({
        componentInfo: {
          component: PatternBuilderComponent
        },
        data: {
          nameGeneratorProperties: this.pathLabelNameGenerationProperties,
          nameGenerator: this.customSettings.pathLabel.nameGenerator
        } as PatternBuilderInput
      } as ContentSwitcherNavigationInput)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((nameGenerator: any) => {
        if (nameGenerator?.parts?.length > 0) {
          this.labelAfterPathIsSameAsBeforeCheckbox = true;
          this.pathLabelsForm.enable({emitEvent: false});

          this.pathLabelsForm.controls.pathLabelBefore.setValue(
            {
              pathLabelType: PathLabelType.TEXT,
              nameGenerator
            },
            {emitEvent: false}
          );
          this.pathLabelsForm.controls.pathLabelAfter.setValue(
            {
              pathLabelType: PathLabelType.TEXT,
              nameGenerator
            },
            {emitEvent: false}
          );

          if (!AssertionUtils.isNullOrUndefined(this.pathLabelInformation)) {
            this.pathLabelsForm.controls.labelFontFamily.setValue(fromReadableToEnumEntry(this.pathLabelInformation.fontSettings.fontFamily), {emitEvent: false});
            this.pathLabelsForm.controls.labelFontWeight.setValue(fromReadableToEnumEntry(this.pathLabelInformation.fontSettings.fontWeight), {emitEvent: false});
            this.pathLabelsForm.controls.labelFontStyle.setValue(fromReadableToEnumEntry(this.pathLabelInformation.fontSettings.fontStyle), {emitEvent: false});
            this.pathLabelsForm.controls.technicalLabelHeightInPicks.setValue(this.pathLabelInformation.labelHeightInPicks, {emitEvent: false});
            this.pathLabelsForm.controls.commercialLabelHeightInMM.setValue(this.pathLabelInformation.labelHeightInMM);
            this.pathLabelsForm.controls.pattern.setValue(this.pathLabelInformation.pattern);
          } else {
            this.pathLabelsForm.controls.labelFontFamily.setValue(this.customSettings.pathLabel.fontFamily, {emitEvent: false});
            this.pathLabelsForm.controls.labelFontWeight.setValue(this.customSettings.pathLabel.fontWeight, {emitEvent: false});
            this.pathLabelsForm.controls.labelFontStyle.setValue(this.customSettings.pathLabel.fontStyle, {emitEvent: false});
            this.pathLabelsForm.controls.commercialLabelHeightInMM.setValue(this.customSettings.pathLabel.heightInMM);
            this.pathLabelsForm.controls.pattern.setValue(null);
          }

          this.pathLabelsForm.markAllAsTouched();
          this.pathLabelInformation = this.getPathLabelInformation();
        }
      });
  }

  private setFormFields(): void {
    this.pathLabelsForm = this.formBuilder.group({
      technicalLabelHeightInPicks: this.formBuilder.control(null, [
        Validators.required,
        isMultipleOfValidator(this.machineQuality.weaveStructure.picksPerColorLine),
        isMultipleOfValidator(this.machineQuality.weaveStructure.sendRepeat),
        Validators.min(this.machineQuality.weaveStructure.picksPerColorLine)
      ]),
      commercialLabelHeightInMM: this.formBuilder.control(null, [Validators.required, Validators.min(1)]),
      pathLabelBefore: this.formBuilder.group({
        pathLabelType: this.formBuilder.control(null),
        nameGenerator: this.formBuilder.control(null)
      }),
      pathLabelAfter: this.formBuilder.group({
        pathLabelType: this.formBuilder.control(null),
        nameGenerator: this.formBuilder.control(null)
      }),
      labelFontFamily: this.formBuilder.control(null, [Validators.required]),
      labelFontWeight: this.formBuilder.control(null, [Validators.required]),
      labelFontStyle: this.formBuilder.control(null, [Validators.required]),
      pattern: this.formBuilder.control(null, Validators.required)
    });

    if (this.areAllFieldsDisabled) {
      this.pathLabelsForm.disable();
    }

    this.syncCommercialLabelHeightWithTechnicalLabelHeightInPicks();
  }

  private setPathLabelAfterAsPathLabelBefore(): void {
    this.pathLabelInformation.pathLabelAfter = cloneDeep(this.pathLabelInformation.pathLabelBefore);
    this.pathLabelsForm.controls.pathLabelAfter.setValue(
      {
        pathLabelType: this.pathLabelInformation.pathLabelBefore.pathLabelType,
        nameGenerator: this.pathLabelInformation.pathLabelBefore.nameGenerator
      },
      {emitEvent: false}
    );
  }

  private getPathLabelNameGenerationProperties(): void {
    this.httpProductionOrderCustomSettings
      .getPathLabelNameGenerationProperties()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((nameGenerationProperties: NameGeneratorProperty[]) => {
        this.pathLabelNameGenerationProperties = nameGenerationProperties;
      });
  }

  private updatePathLabelInformation(): void {
    this.pathLabelInformationChange.emit(this.pathLabelInformation);
  }

  private setFormValues(): void {
    if (!AssertionUtils.isNullOrUndefined(this.pathLabelInformation)) {
      this.pathLabelsForm.reset({
        technicalLabelHeightInPicks: this.pathLabelInformation.labelHeightInPicks,
        commercialLabelHeightInMM: this.pathLabelInformation.labelHeightInMM,
        pathLabelBefore: {
          pathLabelType: this.pathLabelInformation.pathLabelBefore?.pathLabelType,
          nameGenerator: this.pathLabelInformation.pathLabelBefore?.nameGenerator
        },
        pathLabelAfter: {
          pathLabelType: this.pathLabelInformation.pathLabelAfter?.pathLabelType,
          nameGenerator: this.pathLabelInformation.pathLabelAfter?.nameGenerator
        },
        labelFontFamily: fromReadableToEnumEntry(this.pathLabelInformation.fontSettings?.fontFamily),
        labelFontWeight: fromReadableToEnumEntry(this.pathLabelInformation.fontSettings?.fontWeight),
        labelFontStyle: fromReadableToEnumEntry(this.pathLabelInformation.fontSettings?.fontStyle),
        pattern: this.pathLabelInformation.pattern
      });

      const pathLabelInformation = this.getPathLabelInformation();
      this.pathLabelsForm.valueChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => {
        this.updatePathLabelInformation();

        if (!ObjectUtils.isDeepEqual(pathLabelInformation, this.getPathLabelInformation())) {
          this.addProductionSchedule.notifyChangesToInitialPreparationsForm(true);
        }
      });
    }
  }

  private syncCommercialLabelHeightWithTechnicalLabelHeightInPicks(): void {
    this.syncCommercialLabelHeightWithTechnicalLabelHeightInPicksSubscription?.unsubscribe();

    this.syncCommercialLabelHeightWithTechnicalLabelHeightInPicksSubscription = FormValueHelper.syncValuesOfFormFieldsByFactor(
      this.pathLabelsForm.get('commercialLabelHeightInMM'),
      this.pathLabelsForm.get('technicalLabelHeightInPicks'),
      this.factorForConversionFromMillimetersToPicks,
      this.factorForConversionFromPicksToMillimeters,
      this.machineQuality.weaveStructure.picksPerColorLine ?? null
    )
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe();
  }
}
